import React from 'react';
import { FC, ImgHTMLAttributes } from 'react';

export enum IconName {
    BADGE_RIBBON_FOUNDER = 'badge_ribbon_founder',
    BADGE_RIBBON_GIGACHAD = 'badge_ribbon_gigachad',
    BADGE_RIBBON_LIFETIME = 'badge_ribbon_lifetime',
    BADGE_RIBBON_SPONSOR = 'badge_ribbon_sponsor',
    BADGE_RIBBON_VETERAN = 'badge_ribbon_veteran',
    BADGE_FOUNDER = 'badge_founder',
    BADGE_GIGACHAD = 'badge_gigachad.png',
    BADGE_LIFETIME = 'badge_lifetime.png',
    BADGE_SPONSOR = 'badge_sponsor.png',
    BADGE_VETERAN = 'badge_veteran',
    BADGE_NOSTR = 'badge_nostr.png',
    EMAIL = 'email',
    NOSTR_LOGO = 'nostr_logo',
    PILL_BADGE = 'pill_badge',
    TWITTER = 'x_icon',
    ZAP = 'zap',
}

interface IconProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  name: IconName;
  size?: number;
  color?: string;
  secondaryColor?: string;
  className?: string;
  alt?: string;
}

const getColorFilter = (color: string) => {
  // For hex colors
  if (color.startsWith('#')) {
    const rgb = hexToRgb(color);
    if (rgb) {
      return `invert(${rgb.r}) sepia(${rgb.g}) saturate(${rgb.b})`;
    }
  }
  // For named colors
  return `invert(1) sepia(1) saturate(5) hue-rotate(${getHueRotate(color)})`;
};

// Helper function to convert hex to RGB
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16) / 255,
    g: parseInt(result[2], 16) / 255,
    b: parseInt(result[3], 16) / 255
  } : null;
};

// Helper function to get hue rotation for named colors
const getHueRotate = (color: string) => {
  // Add mappings for common colors
  const hueMap: Record<string, number> = {
    'red': 0,
    'blue': 240,
    'green': 120,
    // Add more color mappings as needed
  };
  return `${hueMap[color.toLowerCase()] || 0}deg`;
};

const Icon: FC<IconProps> = ({
  name,
  size = 24,
  color,
  secondaryColor,
  className = '',
  alt = '',
  style,
  ...props
}) => {
  return (
    <img
      src={name.endsWith('.png') ? `/icons/${name}` : `/icons/${name}.svg`}
      width={size}
      height={size}
      className={`icon ${className}`}
      alt={alt}
      style={{
        ...style,
        filter: color ? `brightness(0) saturate(100%) ${getColorFilter(color)}` : undefined,
      }}
      {...props}
    />
  );
};

export default Icon;
